'use client'

import React from 'react'
import { useSession } from 'next-auth/react'
import { usePathname } from 'next/navigation'
import { cn } from '@/lib/utils'
import { useLanguage } from '@/context/LanguageContext'
import { Button } from '@/components/ui/button'
import { Avatar } from '@/components/ui/avatar'
import { Icons } from '@/components/ui/icons'
import { LanguageSelector } from '@/components/layout/LanguageSelector'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'

function getBreadcrumbs(
  pathname: string,
  t: (key: string) => string
): { label: string; href: string }[] {
  const segments = pathname.split('/').filter(Boolean)

  if (segments.length === 0) {
    return [{ label: t('nav.recipe_generator'), href: '/' }]
  }

  const breadcrumbs = [{ label: t('nav.recipe_generator'), href: '/' }]

  let currentPath = ''
  segments.forEach((segment) => {
    currentPath += `/${segment}`
    const label =
      segment === 'nearby-centres'
        ? t('nav.centres')
        : segment === 'about'
          ? t('nav.about')
          : segment === 'history'
            ? t('nav.recipe_history')
            : segment === 'saved'
              ? t('nav.saved_recipes')
              : segment.charAt(0).toUpperCase() + segment.slice(1).replace(/-/g, ' ')

    breadcrumbs.push({ label, href: currentPath })
  })

  return breadcrumbs
}

function SearchInput() {
  return (
    <div
      className={cn(
        'group flex h-9 w-64 items-center gap-2 px-3',
        'rounded-full border border-border/50 bg-white/50',
        'text-sm text-muted-foreground',
        'cursor-default'
      )}
    >
      <Icons.search className='h-4 w-4 text-muted-foreground' strokeWidth={1.5} />
      <span className='flex-1 text-left'>Swaastha AI</span>
    </div>
  )
}

function UserMenu() {
  const { data: session } = useSession()
  const { t } = useLanguage()
  const basePath = process.env.NEXT_PUBLIC_BASE_PATH || ''

  if (!session?.user) return null

  const handleLogout = () => {
    window.location.href = `${basePath}/api/auth/logout`
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          className={cn(
            'group flex items-center gap-1 rounded-full',
            'focus:outline-none focus:ring-2 focus:ring-brand-cornflower/50 focus:ring-offset-2',
            'transition-transform duration-200'
          )}
        >
          <div className='flex items-center gap-3'>
            <div className='hidden flex-col text-right lg:flex'>
              <span className='text-sm font-medium text-foreground'>
                {session.user.name}
              </span>
              <span className='text-xs text-muted-foreground'>
                {t('auth.sso_notice')}
              </span>
            </div>

            <Avatar
              src={session.user.image}
              fallback={session.user.name || session.user.email || '?'}
              size='md'
              showRing
            />
            <Icons.chevronDown
              className={cn(
                'h-4 w-4 text-muted-foreground transition-transform duration-200',
                'group-data-[state=open]:rotate-180 group-hover:translate-y-0.5'
              )}
            />
          </div>
        </button>
      </DropdownMenuTrigger>

      <DropdownMenuContent align='end' className='w-64'>
        <div className='border-b border-border/50 px-3 py-3'>
          <div className='flex items-center gap-3'>
            <Avatar
              src={session.user.image}
              fallback={session.user.name || session.user.email || '?'}
              size='md'
            />
            <div className='min-w-0 flex-1'>
              <p className='truncate text-sm font-medium text-foreground'>
                {session.user.name}
              </p>
              <p className='truncate text-xs text-muted-foreground'>
                {session.user.email}
              </p>
            </div>
          </div>
        </div>

        <div className='py-1'>
          <DropdownMenuItem className='gap-3 rounded-lg px-3 py-2.5'>
            <Icons.user className='h-4 w-4 text-muted-foreground' strokeWidth={1.5} />
            <span>{t('auth.profile')}</span>
          </DropdownMenuItem>
          <DropdownMenuItem className='gap-3 rounded-lg px-3 py-2.5'>
            <Icons.settings className='h-4 w-4 text-muted-foreground' strokeWidth={1.5} />
            <span>{t('auth.settings')}</span>
          </DropdownMenuItem>
        </div>

        <DropdownMenuSeparator />

        <div className='py-1'>
          <DropdownMenuItem
            onClick={handleLogout}
            destructive
            className='gap-3 rounded-lg px-3 py-2.5'
          >
            <Icons.logout className='h-4 w-4' strokeWidth={1.5} />
            <span>{t('auth.signout')}</span>
          </DropdownMenuItem>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

interface HeaderProps {
  onOpenMobileMenu?: () => void
}

export function Header({ onOpenMobileMenu }: HeaderProps) {
  const pathname = usePathname()
  const { t } = useLanguage()
  const breadcrumbs = getBreadcrumbs(pathname || '/', t)

  return (
    <header
      role='banner'
      className={cn(
        'fixed right-4 top-4 z-sticky left-4 md:left-[calc(16rem+1rem)]',
        'rounded-2xl bg-white/70 backdrop-blur-xl border border-white/60 ring-1 ring-black/[0.03] shadow-float',
        'flex items-center justify-between h-14 px-4 lg:px-6'
      )}
    >
      <div className='flex items-center gap-2'>
        <Button
          variant='ghost'
          size='icon-sm'
          onClick={onOpenMobileMenu}
          className='-ml-1 text-muted-foreground hover:text-foreground md:hidden'
          aria-label='Open navigation menu'
        >
          <Icons.menu className='h-5 w-5' strokeWidth={1.5} />
        </Button>

        <Icons.home className='hidden h-4 w-4 text-muted-foreground md:block' strokeWidth={1.5} />
        <nav aria-label='Breadcrumb' className='hidden items-center gap-1 text-sm sm:flex'>
          {breadcrumbs.map((crumb, index) => (
            <React.Fragment key={crumb.href}>
              {index > 0 && (
                <Icons.chevronRight className='h-4 w-4 text-muted-foreground/50' aria-hidden='true' />
              )}
              <span
                className={cn(
                  index === breadcrumbs.length - 1
                    ? 'font-medium text-foreground'
                    : 'text-muted-foreground'
                )}
                aria-current={index === breadcrumbs.length - 1 ? 'page' : undefined}
              >
                {crumb.label}
              </span>
            </React.Fragment>
          ))}
        </nav>
        <span className='text-sm font-medium text-foreground sm:hidden'>
          {breadcrumbs[breadcrumbs.length - 1].label}
        </span>
      </div>

      <div className='flex items-center gap-1 sm:gap-2'>
        <div className='hidden lg:block'>
          <SearchInput />
        </div>

        <LanguageSelector />

        <UserMenu />
      </div>
    </header>
  )
}
